import { ChevronDown, Sparkles, Star } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { apiClient, type AiTraceTask, type MentionAiTrace } from "@/shared/api/api-client";
import type { Mention } from "@/shared/types/mention";
import { PROMPT_TASK_LABELS } from "@/shared/vocabulary/prompt-tasks";
import {
  aiAnsweredNoTheme,
  classificationWhy,
  describeThemeOrigins,
  traceParticipation,
  tracePolarity,
  traceThemes,
  type WhyLine,
  type WhySection
} from "./classification-why";
import { usePremiseTexts } from "./use-premise-texts";

/** Tarefas do rastro, na ordem em que fazem sentido pra quem lê: o
 * veredito primeiro, o detalhe depois. */
const TASK_ORDER = ["polarity_intensity", "role", "themes", "key_message"];

/** Por que uma tarefa não teve chamada de IA. `deterministic` é o caso
 * comum e o mais importante de nomear: a maior parte das menções é
 * resolvida por regra cadastrada, sem gastar IA — e isso é resultado
 * bom, não ausência de informação. */
const REASON_LABELS: Record<string, string> = {
  deterministic: "Resolvido por termo cadastrado, sem consultar a IA.",
  circuit_open: "A IA estava indisponível e a chamada não foi feita.",
  build_failed: "A conexão com a IA não pôde ser criada.",
  error: "A chamada à IA falhou."
};

/**
 * "Por que a classificação é esta", em duas camadas (2026-10-20).
 *
 * 1. **Permanente**: a lista por dimensão, montada da PRÓPRIA menção — a
 *    origem de cada valor e a justificativa curta que o servidor guarda no
 *    Postgres. Sempre aparece, sem requisição.
 * 2. **Rastro**: o registro bruto da IA (prompt e resposta, no Mongo, por
 *    60 dias). Busca ao abrir e falha em silêncio — é secundário, e o
 *    detalhe da menção não pode cair por causa dele.
 *
 * A justificativa que o rastro traz em `parsed_result.justification` NUNCA
 * é mostrada: é o `repr` de um dict Python em inglês ("Theme found
 * deterministically…"), e a dona entendeu como se a IA tivesse dito aquilo.
 */
export function MentionAiTraceSection({
  mention,
  token
}: {
  mention: Mention;
  token: string;
}) {
  const mentionId = mention.id;
  const [trace, setTrace] = useState<MentionAiTrace | null>(null);
  const [loading, setLoading] = useState(true);
  // O texto da premissa só interessa quando a polaridade é da IA com premissa citada.
  const needsPremise = mention.polaritySource === "ai" && mention.polarityRulePosition != null;
  const premiseFor = usePremiseTexts(token, String(mention.brandId), needsPremise);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTrace(await apiClient.mentionAiTrace(token, mentionId));
    } catch {
      /* rastro indisponível não pode derrubar a tela */
      setTrace(null);
    } finally {
      setLoading(false);
    }
  }, [token, mentionId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="why">
      <p className="ai-trace-head">
        <Sparkles size={14} aria-hidden="true"/>
        Por que a classificação é esta
      </p>
      <div className="why-sections">
        {classificationWhy(mention, premiseFor).map(section => (
          <WhySectionBlock key={section.key} section={section}/>
        ))}
      </div>
      <TraceLayer trace={trace} loading={loading}/>
    </div>
  );
}

function WhySectionBlock({ section }: { section: WhySection }) {
  return (
    <section className="why-section" data-dimension={section.key}>
      <h5 className="why-title">{section.title}</h5>
      <ul className="why-lines">
        {section.lines.map(line => (
          <WhyLineItem key={line.key} line={line}/>
        ))}
      </ul>
    </section>
  );
}

function WhyLineItem({ line }: { line: WhyLine }) {
  return (
    <li className="why-line">
      <span className="why-line-main">
        {line.star && (
          <Star
            size={12}
            className="why-star"
            aria-label={line.star === "manual" ? "Subtema em destaque (escolhido)" : "Subtema em destaque (sugestão automática)"}
          />
        )}
        <strong>{line.label}</strong>
        {line.reason && <> — {line.reason}</>}
      </span>
      {line.details.length > 0 && (
        <ul className="why-details">
          {line.details.map((detail, index) => (
            <li key={index}>{detail}</li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** O registro bruto da IA. Sem rastro, a frase dos 60 dias mora SÓ aqui e
 *  não substitui a lista permanente. */
function TraceLayer({ trace, loading }: { trace: MentionAiTrace | null; loading: boolean }) {
  if (loading) {
    return (
      <div className="why-trace">
        <p className="ai-trace-reason">Carregando o registro da IA…</p>
      </div>
    );
  }

  // Sem rastro, a tela diz as TRÊS razões possíveis em vez de escolher
  // uma: o backend não tem como distinguir "não usou IA" de "expirou"
  // de "é anterior à memória", e chutar seria pior que explicar.
  if (!trace || trace.status !== "available" || !trace.tasks.length) {
    return (
      <div className="why-trace">
        <p className="ai-trace-reason">
          Sem registro bruto da IA para esta menção — ela pode ter sido
          classificada sem IA, ser anterior ao registro, ou o registro já ter
          vencido. Ele é mantido por 60 dias.
        </p>
      </div>
    );
  }

  const tasks = [...trace.tasks].sort(
    (a, b) => TASK_ORDER.indexOf(a.task) - TASK_ORDER.indexOf(b.task)
  );

  return (
    <div className="why-trace">
      <p className="why-trace-head">
        Registro bruto da IA — o que foi enviado e respondido, guardado por 60 dias
      </p>
      {trace.edited_manually && (
        <p className="ai-trace-note">
          Esta classificação foi corrigida manualmente depois. O registro abaixo
          mostra o que a IA respondeu, não o que está valendo hoje.
        </p>
      )}
      <ul className="ai-trace-tasks">
        {tasks.map(task => (
          <AiTraceTaskItem key={task.task} task={task}/>
        ))}
      </ul>
    </div>
  );
}

/** O que o resultado da tarefa diz, em frases nossas — nunca o campo
 *  `justification` cru. */
function TaskSummary({ task }: { task: AiTraceTask }) {
  const parsed = task.parsed_result ?? null;
  const calls = task.calls ?? [];

  if (task.task === "themes") {
    const themes = traceThemes(parsed);
    const origins = describeThemeOrigins(themes.origins);
    return (
      <>
        {origins ? (
          <p className="ai-trace-justification">{origins}</p>
        ) : themes.names.length > 0 ? (
          <p className="ai-trace-justification">Subtemas: {themes.names.join(", ")}</p>
        ) : null}
        {aiAnsweredNoTheme(themes, calls) && (
          <p className="ai-trace-reason">
            IA consultada para os subtemas restantes: nenhum se aplica.
          </p>
        )}
      </>
    );
  }

  if (task.task === "role") {
    const participation = traceParticipation(parsed);
    if (!participation.length) return null;
    return (
      <ul className="why-details">
        {participation.map(item => (
          <li key={item.entity}>
            <strong>{item.entity}</strong>
            {item.percentage !== null && `: ${Math.round(item.percentage)}%`}
            {item.text && ` — ${item.text}`}
          </li>
        ))}
      </ul>
    );
  }

  if (task.task === "polarity_intensity") {
    const polarity = tracePolarity(parsed);
    return (
      <>
        {polarity.rule !== null && (
          <p className="ai-trace-justification">Premissa citada: nº {polarity.rule}</p>
        )}
        {polarity.matchedTerms.length > 0 && (
          <p className="ai-trace-justification">
            Termos que casaram: {polarity.matchedTerms.map(term => `"${term}"`).join("; ")}
          </p>
        )}
      </>
    );
  }

  return null;
}

function AiTraceTaskItem({ task }: { task: AiTraceTask }) {
  const [open, setOpen] = useState(false);
  const label = PROMPT_TASK_LABELS[task.task] ?? task.task;
  const calls = task.calls ?? [];

  if (task.status === "expired") {
    return (
      <li className="ai-trace-task">
        <p className="ai-trace-task-head"><strong>{label}</strong></p>
        <p className="ai-trace-reason">
          O registro original desta tarefa já venceu.
        </p>
      </li>
    );
  }

  return (
    <li className="ai-trace-task">
      <p className="ai-trace-task-head">
        <strong>{label}</strong>
        {task.reused && (
          <span className="plain-tag" title="Outra menção deste upload tinha o mesmo texto">
            resposta reaproveitada
          </span>
        )}
      </p>
      <TaskSummary task={task}/>
      {!calls.length && task.calls_reason && (
        <p className="ai-trace-reason">
          {REASON_LABELS[task.calls_reason] ?? task.calls_reason}
        </p>
      )}
      {calls.length > 0 && (
        <>
          <button
            className="ai-trace-toggle"
            type="button"
            onClick={() => setOpen(value => !value)}
            aria-expanded={open}
          >
            <ChevronDown size={14} aria-hidden="true" className={open ? "is-open" : ""}/>
            {open ? "Ocultar" : "Ver"} o que foi enviado e respondido
            {calls.length > 1 && ` (${calls.length} chamadas)`}
          </button>
          {open && (
            <div className="ai-trace-calls">
              {calls.map(call => (
                <div className="ai-trace-call" key={call.index}>
                  {call.error ? (
                    <p className="ai-trace-reason">
                      Esta chamada falhou: {call.error}
                    </p>
                  ) : null}
                  {call.omitted ? (
                    <p className="ai-trace-reason">
                      O texto desta chamada não foi guardado (limite de
                      tamanho do registro).
                    </p>
                  ) : (
                    <>
                      {call.prompt && (
                        <>
                          <span className="ai-trace-call-label">Enviado</span>
                          <pre className="ai-trace-text">{call.prompt}</pre>
                        </>
                      )}
                      {call.response && (
                        <>
                          <span className="ai-trace-call-label">Respondido</span>
                          <pre className="ai-trace-text">{call.response}</pre>
                        </>
                      )}
                      {call.truncated && (
                        <p className="ai-trace-reason">
                          Texto muito longo — guardado até o limite.
                        </p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </li>
  );
}
